import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { CourseCard } from "@/components/CourseCard";
import { LatestNews, LearnerShowcase } from "@/components/HomeDynamic";
import { Seo } from "@/components/Seo";
import { ArrowRight, Search, CreditCard, GraduationCap, Award, CheckCircle2 } from "lucide-react";

const STEPS = [
  { icon: Search, t: "Discover", d: "Browse short courses & intensive bootcamps built for real-world income." },
  { icon: CreditCard, t: "Apply & Pay", d: "Reserve your place in minutes — full payment, deposit or funded access." },
  { icon: GraduationCap, t: "Train", d: "Attend online, in-person or hybrid. Everything in one dashboard." },
  { icon: Award, t: "Certify", d: "Complete, earn your certificate and unlock your next step." },
];

export default function Home() {
  const [courses, setCourses] = useState([]);

  useEffect(() => {
    api.get("/courses", { params: { featured: true } })
      .then((r) => {
        if (r.data.length) return setCourses(r.data.slice(0, 4));
        return api.get("/courses").then((res) => setCourses(res.data.slice(0, 4)));
      })
      .catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      <Seo
        title="Practical online courses with CPD certificates"
        description="Get Skilled in Days Not Years — short, career-focused online courses with CPD-accredited certificates. Discover, enrol, learn and get certified, all from your phone."
        path="/"
      />
      {/* Hero */}
      <section className="relative border-b border-[var(--line)] overflow-hidden">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 grid lg:grid-cols-12 gap-8 items-center py-16 lg:py-24">
          <div className="lg:col-span-7 animate-fade-up">
            <span className="inline-block text-xs tracking-label uppercase font-semibold text-[var(--accent)] mb-5">
              Practical skills. Real opportunities.
            </span>
            <h1 className="font-display font-extrabold text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight">
              Get skilled in <span className="text-[var(--accent)]">days</span>, not years.
            </h1>
            <p className="mt-6 text-lg text-[var(--ink-2)] max-w-xl">
              Career-focused bootcamps that help you discover a skill, secure a place, train physically or virtually, certify and progress — all from your phone in under 4 weeks.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/courses" className="inline-flex items-center gap-2 bg-[var(--accent)] text-white font-semibold px-6 py-3.5 hover:bg-[var(--accent-hover)] transition-colors" data-testid="hero-browse-btn">
                Browse courses <ArrowRight size={18} />
              </Link>
              <Link to="/register" className="inline-flex items-center gap-2 border border-[var(--ink)] font-semibold px-6 py-3.5 hover:bg-[var(--ink)] hover:text-white transition-colors" data-testid="hero-register-btn">
                Create free account
              </Link>
            </div>
            <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm text-[var(--ink-2)]">
              {["Discounts", "Secure with deposits", "Certificate of completion", "CPD accredited certificate"].map((x) => (
                <span key={x} className="inline-flex items-center gap-1.5"><CheckCircle2 size={15} className="text-[var(--accent)]" /> {x}</span>
              ))}
            </div>
          </div>
          <div className="lg:col-span-5 relative animate-fade-up" style={{ animationDelay: "0.15s" }}>
            <div className="relative border border-[var(--ink)]">
              <img src="https://images.unsplash.com/photo-1758611973525-b5a86f468b5e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000" alt="Learner on phone" className="w-full aspect-[4/5] object-cover" />
              <div className="absolute -bottom-5 -left-5 bg-[var(--ink)] text-white p-5 hidden sm:block">
                <div className="font-display font-extrabold text-3xl">7</div>
                <div className="text-xs uppercase tracking-wide text-white/70">days to job-ready skills</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="mx-auto max-w-7xl px-4 sm:px-6 py-16 lg:py-24">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-12">
          <h2 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight">One joined-up journey</h2>
          <p className="text-[var(--ink-2)] max-w-sm text-sm">Discover → Apply → Pay → Enrol → Attend → Learn → Complete → Certify → Progress.</p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 border-l border-t border-[var(--line)]">
          {STEPS.map((s, i) => (
            <div key={s.t} className="border-r border-b border-[var(--line)] p-7 hover:bg-white transition-colors group" data-testid={`step-${i}`}>
              <div className="flex items-center justify-between mb-6">
                <span className="grid place-items-center h-11 w-11 bg-[var(--alt)] group-hover:bg-[var(--accent)] group-hover:text-white transition-colors"><s.icon size={20} /></span>
                <span className="font-display font-extrabold text-2xl text-[var(--line)]">0{i + 1}</span>
              </div>
              <h3 className="font-display font-bold text-lg mb-1">{s.t}</h3>
              <p className="text-sm text-[var(--ink-2)]">{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Featured courses */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 pb-8">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <h2 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight">Featured bootcamps</h2>
          <Link to="/courses" className="text-sm font-semibold inline-flex items-center gap-1.5 hover:text-[var(--accent)]" data-testid="home-view-all">
            View all <ArrowRight size={16} />
          </Link>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {courses.map((c) => <CourseCard key={c.id} course={c} />)}
        </div>
      </section>

      <LatestNews />
      <LearnerShowcase />

      {/* CTA */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 py-16">
        <div className="bg-[var(--ink)] text-white p-10 sm:p-16 relative overflow-hidden grain">
          <div className="relative max-w-2xl">
            <h2 className="font-display font-extrabold text-3xl sm:text-5xl leading-tight tracking-tight">Secure your place. Build your next income stream.</h2>
            <p className="mt-4 text-white/70">Train today. Build your future. From curiosity to certification.</p>
            <Link to="/register" className="mt-8 inline-flex items-center gap-2 bg-[var(--accent)] text-white font-semibold px-6 py-3.5 hover:bg-[var(--accent-hover)] transition-colors" data-testid="cta-register">
              Get started free <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
